import {
  HttpErrorResponse,
  type HttpEvent,
  type HttpHandler,
  type HttpInterceptor,
  type HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { type Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AuthService } from '@auth/repository';
import { isLoggedInUser } from '@auth/repository/auth.repository';
import { SessionPollingService } from '@auth/repository/session-polling.service';
import { ERROR_TOKEN_NAME } from '@core/interceptors/app-interceptor/app.interceptor';
import { NotifyService } from '@core/services/notifyService.service';
import { environment } from '@env/environment';
import { DIALOG_SIZE } from '@shared/constants/ume-constants';
import { ConfirmationDialogComponent } from '@shared/dialogs/confirmation-dialog/confirmation-dialog.component';

@Injectable()
export class ErrorHandlingInterceptor implements HttpInterceptor {
  private readonly authService: AuthService = inject(AuthService);
  private readonly session: SessionPollingService = inject(SessionPollingService);
  private readonly notifyService: NotifyService = inject(NotifyService);
  private readonly dialog: MatDialog = inject(MatDialog);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse) {
          switch (err.status) {
            case 0:
              this.notifyService.notifyErrorOnly(
                'Your organisation is blocking access to ume. Please contact your IT support.'
              );
              break;
            case 401: {
              // stop polling session API
              this.session.stopPolling();

              // log out only for internal restriction
              const isUmeRequest = request.url.includes(environment.API);
              const isAlreadyOpen = Boolean(this.dialog.openDialogs.length);
              const isLoggedIn = isLoggedInUser();

              if (
                isUmeRequest &&
                !isAlreadyOpen &&
                isLoggedIn &&
                !request.url.endsWith('/auth/login')
              ) {
                const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                  data: {
                    title: 'Session expired',
                    text: `
                        <p class="text">Your session has expired.</p>
                        <p class="text">Please log in again.</p>
                      `,
                    approveLabel: 'Sign in',
                    submitOnly: true,
                  },
                  autoFocus: true,
                  width: DIALOG_SIZE.MODAL_SM,
                });

                dialogRef.afterClosed().subscribe(() => {
                  this.authService.logout();
                });
              }
              break;
            }
            case 403:
              if (err.headers.get(ERROR_TOKEN_NAME) !== 'TokenExpired') {
                this.notifyService.notifyCaution(
                  `Request id: ${err.status}` +
                    '\n' +
                    `${err.error.auditTrailId ?? err.error.stackTrace}`
                );
              }
              break;
            case 404:
              this.notifyService.notifyErrorOnly('Not implemented yet');
              break;
            case 400:
              this.notifyService.notifyErrorOnly(err.error.message);
              break;
            case 500: {
              const message = err.error.message || 'Something went wrong';
              this.notifyService.notifyErrorOnly(message);
              break;
            }
            //Special case for handling 503 errors in fetch file service
            case 503:
              break;
            default:
              this.notifyService.notifyCaution(
                `Request id: ${err.status}` +
                  '\n' +
                  `${err.error.auditTrailId ?? err.error.stackTrace}`
              );
              break;
          }
        }

        return throwError(() => err);
      })
    );
  }
}
